import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';

const RelatedTags = ({ tag, limit = 5 }) => {
  const [relatedTags, setRelatedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!tag) {
      setRelatedTags([]);
      setSearched(false);
      return;
    }

    const fetchRelatedTags = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/tags/related/${encodeURIComponent(tag)}?limit=${limit}`);
        setRelatedTags(response.data.related || []);
        setSearched(true);
      } catch (err) {
        console.error('Error fetching related tags:', err);
        setError('Failed to fetch related tags');
        setRelatedTags([]);
        setSearched(true);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedTags();
  }, [tag, limit]);

  if (!tag) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Finding related tags...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
        {error}
      </Typography>
    );
  }

  if (searched && relatedTags.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No related tags found for "{tag}"
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
        Related tags:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {relatedTags.map((related) => (
          <Chip
            key={related.tag}
            label={related.tag}
            component={RouterLink}
            to={`/search?q=${encodeURIComponent(related.tag)}`}
            clickable
            size="small"
            sx={{ 
              backgroundColor: '#f0f7ff',
              '&:hover': {
                backgroundColor: '#0097A7',
                color: 'white',
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default RelatedTags;
